import { useCallback, useMemo, useState } from "react";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 15, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

export interface UsePaginationOptions {
  initialPage?: number;
  initialLimit?: number;
  pageSizeOptions?: number[];
}

export interface UsePaginationReturn {
  page: number;
  limit: number;
  total: number;
  setTotal: (total: number) => void;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  pageNumbers: (number | "ellipsis")[];
  goToPage: (page: number) => void;
  setLimit: (limit: number) => void;
  pageSizeOptions: number[];
}

function getPageNumbers(
  page: number,
  totalPages: number
): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | "ellipsis")[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) {
    pages.push("ellipsis");
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  if (end < totalPages - 1) {
    pages.push("ellipsis");
  }
  if (totalPages > 1) {
    pages.push(totalPages);
  }
  return pages;
}

export function usePagination(
  options: UsePaginationOptions = {}
): UsePaginationReturn {
  const {
    initialPage = 1,
    initialLimit = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  } = options;

  const [page, setPage] = useState(initialPage);
  const [limit, setLimitState] = useState(initialLimit);
  const [total, setTotal] = useState(0);

  const totalPages = Math.ceil(total / limit) || 1;
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;

  const pageNumbers = useMemo(
    () => getPageNumbers(page, totalPages),
    [page, totalPages]
  );

  const goToPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const setLimit = useCallback((newLimit: number) => {
    setLimitState(newLimit);
    setPage(1);
  }, []);

  return {
    page,
    limit,
    total,
    setTotal,
    totalPages,
    hasNextPage,
    hasPrevPage,
    pageNumbers,
    goToPage,
    setLimit,
    pageSizeOptions,
  };
}
